/**
 * Validation for "Add by IP address" on a desktop register.
 *
 * This is the **operator-facing** check: it explains, before anything leaves
 * the renderer, why an address cannot be a printer. It is not the security
 * boundary — the main process enforces the same rule again
 * (`electron/main/services/star-lan/destination.ts`) and would refuse anything
 * this function let through.
 *
 * Hostnames are deliberately not accepted. Star documents no stable hostname
 * for its LAN printers, and resolving one in the renderer would let DNS decide
 * where the printer channel connects. The printer's self-test page prints its
 * IP address; that is what the operator types.
 */
import type { StarPrinter } from 'src/services/printers';

/** Star's raw-print port; the only one a desktop register prints on. */
export const STAR_LAN_PORT = 9100;

export type LanAddressCheck =
  | { readonly ok: true; readonly ipAddress: string; readonly port: number }
  | { readonly ok: false; readonly message: string };

const IPV4_OCTET = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

function octets(host: string): number[] | null {
  const parts = host.split('.');
  if (parts.length !== 4 || parts.some((part) => !IPV4_OCTET.test(part))) return null;
  return parts.map((part) => Number.parseInt(part, 10));
}

function isPrivate([a, b]: number[]): boolean {
  if (a === 10) return true;
  if (a === 172 && b !== undefined && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

export function checkLanPrinterAddress(input: string): LanAddressCheck {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: 'Enter the IP address from the printer’s self-test page.' };
  }

  const [host = '', portPart, ...rest] = trimmed.split(':');
  if (rest.length > 0) return { ok: false, message: 'That does not look like an IPv4 address.' };

  const parsed = octets(host);
  if (parsed === null) {
    return /[a-z]/i.test(host)
      ? {
          ok: false,
          message:
            'Enter the printer’s IP address (for example 192.168.1.50), not its name. It is printed on the self-test page.',
        }
      : {
          ok: false,
          message: 'That does not look like an IPv4 address, for example 192.168.1.50.',
        };
  }

  if (!isPrivate(parsed)) {
    return {
      ok: false,
      message:
        'Printers can only be added on this shop’s private network (addresses such as 192.168.x.x, 10.x.x.x or 172.16–31.x.x).',
    };
  }

  if (portPart !== undefined && portPart !== String(STAR_LAN_PORT)) {
    return {
      ok: false,
      message: `Star network printers print on port ${String(STAR_LAN_PORT)}. Leave the port off the address.`,
    };
  }

  return { ok: true, ipAddress: host, port: STAR_LAN_PORT };
}

/**
 * The printer record for an address the operator typed, in exactly the shape
 * the main process describes a LAN printer (`lan:<ip>:<port>`), so a printer
 * saved this way and one saved from discovery are the same record. Only what
 * the operator supplied is filled in — no model, serial or MAC is invented.
 */
export function printerForAddress(ipAddress: string, port: number = STAR_LAN_PORT): StarPrinter {
  const identifier = `lan:${ipAddress}:${String(port)}`;
  return {
    identifier,
    interfaceType: 'lan',
    model: 'unknown',
    ipAddress,
    connection: { interfaceType: 'lan', identifier, ipAddress, address: ipAddress, port },
    isMock: false,
  };
}

/** "mC-Print3" when the printer reported one; otherwise an honest generic label. */
export function lanPrinterTitle(printer: Pick<StarPrinter, 'model' | 'modelName'>): string {
  const model = printer.modelName ?? printer.model;
  return model && model !== 'unknown' ? model : 'Star network printer';
}
